<script setup lang="ts">
import gsap from 'gsap';
import EmailLink from 'src/components/uikit/EmailLink.vue';

import { menuItems } from 'src/data/menu';

const title = ':PtBurg* • Арт студия • Дизайн и разработка';
const description =
  'Создаем решения реального времени c 3D и анимацией. Интернет – это искусство.';

const siteUrl = 'https://ptburg.com';
const image = `${siteUrl}/og-image.png`;

const route = useRoute();
const canonicalUrl = computed(() => `${siteUrl}${route.path}`);

useMeta(() => ({
  title,
  meta: {
    description: { name: 'description', content: description },
    ogTitle: { property: 'og:title', content: title },
    ogDescription: {
      property: 'og:description',
      content: description,
    },
    ogImage: {
      property: 'og:image',
      content: image,
    },
    ogUrl: { property: 'og:url', content: canonicalUrl.value },
    ogType: { property: 'og:type', content: 'website' },
    ogSiteName: { property: 'og:site_name', content: 'PtBurg' },
    ogLocale: { property: 'og:locale', content: 'ru_RU' },
    twitterCard: { name: 'twitter:card', content: 'summary_large_image' },
    twitterTitle: { name: 'twitter:title', content: title },
    twitterDescription: {
      name: 'twitter:description',
      content: description,
    },
    twitterImage: { name: 'twitter:image', content: image },
  },
  link: {
    canonical: { rel: 'canonical', href: canonicalUrl.value },
  },
}));

const { width } = useWindowSize();

const { showCookieNotification } = useCookieConsent();

const isClient = ref(false);

const rightDrawerRef = ref();

const isRightDrawerOpen = ref(false);

const isDarkLayout = computed(() => route.path.startsWith('/speed'));

let gsapCtx: gsap.Context;

onMounted(() => {
  isClient.value = true;
  showCookieNotification();

  gsapCtx = gsap.context(() => {});
});

onUnmounted(() => {
  gsapCtx?.revert();
});

const rightDrawerToggle = () => {
  rightDrawerRef.value.toggle();
};

watch(isRightDrawerOpen, (val) => {
  if (val) {
    gsapCtx.add(() => {
      gsap.fromTo(
        '.drawer-menu .char',
        {
          opacity: 0,
          rotationX: 120,
          rotationY: -90,
          translateX: -80,
        },
        {
          ease: 'expo.out',
          opacity: 1,
          rotationX: 0,
          rotationY: 0,
          translateX: 0,
          stagger: {
            each: 0.03,
            from: 0,
          },
        },
      );
    });
  }
});

const menuItemMouseEnter0 = (event: MouseEvent) => {
  const q = gsap.utils.selector(event.target);

  gsapCtx.add(() => {
    gsap.fromTo(
      q('.char'),
      {
        opacity: 0,
        rotationX: -90,
        yPercent: 50,
      },
      {
        ease: 'power1.inOut',
        opacity: 1,
        rotationX: 0,
        yPercent: 0,
        stagger: {
          each: 0.03,
          from: 0,
        },
      },
    );
  });
};
</script>

<template lang="pug">
q-layout(
  :style="isDarkLayout ? 'background-color: #121212;' : 'background-color: #F7F7F7;'"
  :class="{ 'layout--dark': isDarkLayout }"
  )
  // Header
  q-header(
    :class="['fixed', { 'bg-transparent': !isDarkLayout, 'header--dark': isDarkLayout }]"
    )
    q-toolbar(
      :class="['row', 'justify-between', 'pa-4', { 'bg-transparent': !isDarkLayout }]"
      )
      //- q-img.toolbar__logo(
        src="~assets/logo.svg"
        no-spinner
        @click.stop="router.push('/')").w-150px.h-46px.cursor-pointer

      //- p.text-red {{ isRightDrawerOpen }}
      q-space
      MenuBtn(
        style="z-index: 4000;"
        @click="rightDrawerToggle")
      //- MainMenu.q-mr-md
  // Right Drawer
  //- transition(
    appear
    enter-active-class="animated fadeIn"
    leave-active-class="animated fadeOut")
  q-drawer#right-drawer(
      ref="rightDrawerRef"
      v-model="isRightDrawerOpen"
      side="right"
      overlay
      behavior="mobile"
      :width="width < 600 ? width : 600"
      ).drawer-right
      //- FBM noise фон с огоньком у курсора (нижний слой)
      DrawerCanvasBg(:active="isRightDrawerOpen")
      //- 3D звёздное поле с глубиной (верхний слой, прозрачный)
      TreDrawerStars(:active="isRightDrawerOpen")
      //- Содержимое drawer
      .drawer-right__container
        q-toolbar.drawer-right__toolbar().row.justify-end.pa-4
          MenuBtn(
            icon="close"
            @click="rightDrawerToggle")
        .drawer-right__menu-container.flex.flex-center.items-center.w-full.mt-4
          q-list.drawer-menu().alegreya-400.w-full
            q-item.drawer-menu__item(
              v-for="(item,i) in menuItems"
              :key="`drawer-menu-item-${item.name}-${i}`"
              ref="drawerMenuItemRef"
              :to="item.path"
              active-class="drawer-menu__item--active"
              clickable
              @mouseenter="menuItemMouseEnter0"
              ).px-48px.py-4.lg-px-80px
              q-item-section
                //- span.char {{ item.label }}
                SplitSpanLetters.drawer-menu__item__label(
                  :letters="item.label"
                  item-class="char")
                  //- :item-class="`char-${i}`")
              q-item-section(side)
                .w-48px.text-center
                  span.drawer-menu__item__number ({{ i+1 }})
          .drawer-right__social-btns().mt-16
            EmailLink(
              @click="$yandexMetrika.reachGoal('menu_click__email')"
              ).text-white
            //- SocialBtns

  // Pages
  q-page-container
    Suspense
      template(#fallback)
        LoadingSkeleton(bg-color="#F7F7F7")
      router-view

  // Footer
  //- q-footer().bg-transparent
    q-toolbar
      MenuBar.gt-md

  // Dialogs
  FormDialog
  SuccessDialog

  // AI Chat Widget
  AiChat
</template>

<style lang="sass">


// (style="height: calc(100dvh - 80px)")

// .q-drawer--right
//   widht: 100% !important
//   max-width: 600px !important


.header--dark
  background: rgba(18, 18, 18, 0.8) !important
  backdrop-filter: blur(20px) saturate(1.2)
  -webkit-backdrop-filter: blur(20px) saturate(1.2)
  border-bottom: 1px solid rgba(255, 255, 255, 0.06)

.drawer-right
  max-height: 100dvh
  position: relative
  // Тёмный фон — совпадает с canvas-фоном, чтобы панель не была белой при выезде
  background-color: #121212
  // Старый CSS-градиент — сохранён как fallback для WebGL
  // background: linear-gradient(322.02deg, #211F20 28.08%, #5C565B 71.92%)

.drawer-right__container
  position: relative
  z-index: 1

.q-drawer.q-drawer--right
  // width: 600px; transform: translateX(0px);
  // transition: all 0.6s ease-out !important
  // transition: all 0.6s ease-in !important
  transition: all 0.8s !important


.drawer-menu
  font-family: Alegreya
  font-size: 40px
  font-weight: 400
  line-height: 40px
  text-align: left

// .q-focusable:focus, .q-manual-focusable--focused, .q-hoverable:hover
//     > .q-focus-helper
//     background-color: #1f2122 !important
//     background: #1f2122 !important

.drawer-menu__item
  color: white

.drawer-menu__item__label
  // perspective: 2000px
  font-size: 40px
  // font-size: 64px



// .drawer-menu__item:hover
//   background-color: #1f2122 !important

.drawer-menu__item--active.q-router-link--exact-active
  color: $primary
  .drawer-menu__item__number
    color: $primary

.drawer-menu__item__number
  font-family: Alegreya
  font-size: 18px
  font-weight: 400
  line-height: 18px
  color: white
</style>
